import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { useResolvedOwnerId } from '@/hooks/analytics/useResolvedOwnerId';
import { briefWorkspaceService } from '@/services/data/BriefWorkspaceService';

export const briefWorkspaceQueryKey = (userId: string | null | undefined, briefId: string | null | undefined) =>
  ['brief-workspace', userId ?? null, briefId ?? null] as const;

export function useBriefWorkspace(briefId: string | null | undefined) {
  const { data: ownerId } = useResolvedOwnerId(Boolean(briefId));
  return useQuery({
    queryKey: briefWorkspaceQueryKey(ownerId, briefId),
    enabled: Boolean(ownerId && briefId),
    queryFn: () => briefWorkspaceService.getWorkspace(briefId!),
  });
}

export function useUpdateBriefIdentity(briefId: string | null | undefined) {
  const { data: ownerId } = useResolvedOwnerId(Boolean(briefId));
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { name: string; objective: string | null; expectedVersion: number }) => {
      if (!briefId) throw new Error('Brief not found');
      return briefWorkspaceService.updateIdentity({ briefId, ...input });
    },
    onSuccess: (workspace) => {
      queryClient.setQueryData(briefWorkspaceQueryKey(ownerId, briefId), workspace);
    },
  });
}

export function useSetBriefArchived(briefId: string | null | undefined) {
  const { data: ownerId } = useResolvedOwnerId(Boolean(briefId));
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { archived: boolean; expectedVersion: number }) => {
      if (!briefId) throw new Error('Brief not found');
      return briefWorkspaceService.setArchived({ briefId, ...input });
    },
    onSuccess: (workspace) => {
      queryClient.setQueryData(briefWorkspaceQueryKey(ownerId, briefId), workspace);
      void queryClient.invalidateQueries({ queryKey: ['campaigns', 'briefs'] });
    },
  });
}

export function useReviewBriefAdUnit(briefId: string | null | undefined) {
  const { data: ownerId } = useResolvedOwnerId(Boolean(briefId));
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: { adUnitId: string; status: string; reviewNote?: string | null }) =>
      briefWorkspaceService.reviewAdUnit(input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: briefWorkspaceQueryKey(ownerId, briefId) });
    },
  });
}
